export const vocalsIndianContemporarySyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation in Contemporary Indian Singing)",
    objective:
      "To establish healthy contemporary vocal technique, basic pitch accuracy, clear diction in Indian languages, emotional expression and confidence in singing Indian film and light music.",
    assessmentFocus: [
      "Healthy voice production",
      "Pitch accuracy",
      "Clarity of lyrics",
      "Emotional expression",
      "Confidence and musicality",
    ],
    examFormat: {
      title: "Level 1 — Practical Rubric",
      totalMarks: 100,
      components: [
        { criteria: "Pitch Accuracy & Basic Intonation", marks: 25 },
        { criteria: "Vocal Tone & Freedom", marks: 20 },
        { criteria: "Breath Control (Basic)", marks: 15 },
        { criteria: "Lyric Clarity & Diction", marks: 15 },
        { criteria: "Rhythm Awareness", marks: 10 },
        { criteria: "Confidence & Expression", marks: 15 },
      ],
      notes: "Assessment based on voice quality, expression, and technique.",
      passCriteria: "Minimum 50%",
    },
    practicalSections: [
      {
        title: "Vocal & body fundamentals",
        items: [
          "Understanding the contemporary Indian singing voice",
          "Natural standing posture",
          "Body relaxation and tension release",
          "Jaw, tongue and neck freedom",
        ],
      },
      {
        title: "Breathing & support",
        items: [
          "Natural diaphragmatic breathing",
          "Breath–voice coordination",
          "Controlled airflow for sustained phrases",
          "Avoidance of throat tension and breath pushing",
        ],
      },
      {
        title: "Tone production & pitch",
        items: [
          "Natural vocal onset",
          "Balanced chest-dominant tone",
          "Avoidance of excessive nasality and strain",
          "Accurate pitch matching within a comfortable range",
        ],
      },
      {
        title: "Exercises & scales",
        items: [
          "Major and natural minor scale patterns (comfortable keys)",
          "Five-note exercises",
          "Simple arpeggios",
          "Sustained tone exercises",
          "Basic agility patterns",
        ],
      },
      {
        title: "Language & style",
        items: [
          "Clear pronunciation in Hindi and one regional language (student choice)",
          "Vowel clarity and consonant articulation",
          "Introduction to film music and light contemporary styles",
        ],
      },
      {
        title: "Rhythm & performance",
        items: [
          "Steady tempo in 4/4 and 3/4",
          "Singing with metronome or backing track",
          "Confident solo singing and basic stage awareness",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Two songs: one learner’s choice (Indian film / contemporary) and one technical song (examiner-guided)",
          "Original song permitted (optional)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music & voice awareness",
        items: [
          "Difference between singing and speaking voice",
          "Basic pitch awareness and melody–lyrics relationship",
        ],
      },
      {
        title: "Song structure & rhythm",
        items: [
          "Basic idea of verse, chorus and antara",
          "Introduction to pulse and tempo",
        ],
      },
      {
        title: "Vocal health",
        items: [
          "Hydration and vocal rest",
          "Safe warm-ups and cool-downs",
          "Avoiding vocal strain and healthy practice habits",
        ],
      },
    ],
  },
  2: {
    id: 2,
    title: "Level 2 — Basic Development (Control & Musical Growth)",
    objective:
      "To develop greater vocal control, pitch stability, expanded range and clearer diction for Indian contemporary and film repertoire.",
    assessmentFocus: [
      "Vocal stability",
      "Pitch control",
      "Emotional delivery",
      "Technical safety",
    ],
    examFormat: {
      title: "Level 2 — Practical Rubric",
      totalMarks: 100,
      components: [
        { criteria: "Pitch Stability & Range Control", marks: 25 },
        { criteria: "Tone Quality & Mix Balance", marks: 20 },
        { criteria: "Breath Management", marks: 15 },
        { criteria: "Musical Phrasing", marks: 15 },
        { criteria: "Rhythm & Timing", marks: 10 },
        { criteria: "Stage Confidence", marks: 15 },
      ],
      notes: "Assessment focuses on vocal stability, pitch control, and emotional delivery.",
      passCriteria: "Minimum 50%",
    },
    practicalSections: [
      {
        title: "Vocal technique development",
        items: [
          "Improved posture and body alignment",
          "Reduced throat tension",
          "Balanced chest–mix coordination (introductory)",
        ],
      },
      {
        title: "Breath & tone",
        items: [
          "Controlled inhalation and exhalation",
          "Sustained breath for longer phrases",
          "Fuller and more stable tone with reduced breathiness",
        ],
      },
      {
        title: "Range, agility & style",
        items: [
          "Gentle extension of vocal range",
          "Stepwise and small interval leaps",
          "Introduction to light melodic ornamentation",
          "Major, natural minor and pentatonic scale patterns",
        ],
      },
      {
        title: "Language & expression",
        items: [
          "Pronunciation in Hindi and one additional Indian language",
          "Emotional delivery aligned with lyrics",
          "Control of consonants without tension",
        ],
      },
      {
        title: "Rhythm & repertoire",
        items: [
          "Singing confidently in 4/4 and 6/8",
          "Maintaining tempo with backing tracks",
          "Three songs in contrasting moods (slow, moderate and examiner-guided technical)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Vocal awareness",
        items: [
          "Chest vs mix voice (concept)",
          "Range vs comfort understanding",
        ],
      },
      {
        title: "Song interpretation & health",
        items: [
          "Lyric meaning and emotional intent",
          "Importance of breath control in film singing",
          "Safe practice duration and recovery strategies",
        ],
      },
    ],
  },
  3: {
    id: 3,
    title: "Level 3 — Intermediate Performance (Technique & Musical Identity)",
    objective:
      "To build consistent technique, stylistic confidence, controlled dynamics and an emerging musical identity in Indian film and contemporary styles.",
    assessmentFocus: [
      "Technical consistency",
      "Musical maturity",
      "Stylistic confidence",
      "Safe vocal production",
    ],
    examFormat: {
      title: "Level 3 — Theory + Practical",
      totalMarks: 100,
      practicalMarks: 70,
      theoryMarks: 30,
      components: [
        { criteria: "Practical — Technical Consistency", marks: 15 },
        { criteria: "Practical — Pitch, Range & Control", marks: 15 },
        { criteria: "Practical — Expression & Interpretation", marks: 15 },
        { criteria: "Practical — Rhythm & Groove", marks: 10 },
        { criteria: "Practical — Performance Confidence", marks: 10 },
        { criteria: "Practical — Vocal Health Awareness", marks: 5 },
        { criteria: "Theory (Short Answer + Descriptive)", marks: 30 },
      ],
      notes: "Theory covers diaphragmatic breathing, pitch and intonation, vocal warm-up, speaking vs. singing voice, phrasing, breath control, role of emotion, vocal care, and song structure.",
      passCriteria: "Minimum 55% (Theory + Practical separately)",
    },
    practicalSections: [
      {
        title: "Advanced technique foundations",
        items: [
          "Stable mix coordination",
          "Smooth register transitions",
          "Controlled natural vibrato",
          "Dynamic variation across phrases",
        ],
      },
      {
        title: "Breath, tone & agility",
        items: [
          "Longer sustained phrases",
          "Improved stamina",
          "Agility exercises for controlled slides and light gamaks",
        ],
      },
      {
        title: "Language, style & repertoire",
        items: [
          "Confident singing in Hindi, English and one regional language",
          "3–4 songs covering slow emotional, rhythm-focused and technical pieces",
          "Original compositions encouraged",
        ],
      },
    ],
    theorySections: [
      {
        title: "Vocal & music concepts",
        items: [
          "Basic vocal anatomy (practical understanding)",
          "Dynamics and expression in Indian contemporary music",
        ],
      },
      {
        title: "Song structure & performance",
        items: [
          "Form awareness in Indian film songs",
          "Practice planning and performance psychology (introductory)",
        ],
      },
    ],
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced Performance (Control, Style & Artistry)",
    objective:
      "To develop advanced vocal control, stylistic versatility, expressive depth and confident public performance with strong musical identity.",
    assessmentFocus: [
      "Technical reliability",
      "Stylistic clarity",
      "Expressive depth",
      "Performance confidence",
    ],
    examFormat: {
      title: "Level 4 — Theory + Practical",
      totalMarks: 100,
      practicalMarks: 70,
      theoryMarks: 30,
      components: [
        { criteria: "Practical — Advanced Vocal Technique", marks: 20 },
        { criteria: "Practical — Stylistic Clarity", marks: 15 },
        { criteria: "Practical — Emotional Depth", marks: 15 },
        { criteria: "Practical — Rhythm & Musicality", marks: 10 },
        { criteria: "Practical — Stage & Mic Technique", marks: 10 },
        { criteria: "Theory (Short Answer + Descriptive)", marks: 30 },
      ],
      notes: "Theory covers vocal mix, microphone technique, dynamics, stylistic authenticity, live vs. studio singing, emotion and vocal tone, managing vocal fatigue, and role of rhythm.",
      passCriteria: "Minimum 55% (Theory + Practical separately)",
    },
    practicalSections: [
      {
        title: "Advanced vocal technique",
        items: [
          "Strong, balanced mix coordination",
          "Clean register transitions",
          "Controlled vibrato usage",
          "Dynamic contrast and tonal shading",
        ],
      },
      {
        title: "Style, rhythm & repertoire",
        items: [
          "Stylistic differentiation between romantic, dramatic, soft pop and semi-classical film songs",
          "Rhythmic confidence in 4/4, 6/8 and rubato-feel songs",
          "Five songs across emotional, rhythmic and semi-classical styles",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced vocal awareness",
        items: [
          "Register blending and dynamic control",
          "Managing vocal fatigue and safe high-intensity singing",
        ],
      },
      {
        title: "Interpretation & professional awareness",
        items: [
          "Difference between live and studio singing",
          "Role of emotion and rhythm in Indian contemporary voice work",
        ],
      },
    ],
  },
  5: {
    id: 5,
    title: "Level 5 — Professional Performance (Artist Readiness Level)",
    objective:
      "To certify professional-level vocal control, stylistic authority, emotional maturity and performance stamina for concerts and recordings in Indian film and contemporary settings.",
    assessmentFocus: [
      "Professional consistency",
      "Artistic individuality",
      "Technical mastery",
      "Performance endurance",
    ],
    examFormat: {
      title: "Level 5 — Professional Certification",
      totalMarks: 100,
      practicalMarks: 80,
      theoryMarks: 20,
      components: [
        { criteria: "Practical — Professional Vocal Control", marks: 25 },
        { criteria: "Practical — Artistic Interpretation", marks: 20 },
        { criteria: "Practical — Endurance & Consistency", marks: 15 },
        { criteria: "Practical — Rhythm & Groove Authority", marks: 10 },
        { criteria: "Practical — Stage Presence", marks: 10 },
        { criteria: "Theory (Long Answer / Analytical)", marks: 20 },
      ],
      notes: "Theory covers vocal challenges in professional film singing, building a sustainable singing career, interpretation vs. imitation, and managing long performances safely.",
      passCriteria: "Minimum 60% + mandatory recital completion",
    },
    practicalSections: [
      {
        title: "Mastery-level technique",
        items: [
          "Effortless vocal production with reliable mix and stylistic effects",
          "Wide usable range with endurance",
        ],
      },
      {
        title: "Artistic identity & repertoire",
        items: [
          "Clear artistic voice across 6 songs (slow emotional, rhythm-intensive, semi-classical-influenced, fast-tempo and mandated original composition)",
          "Leadership in working with accompaniment and tracks",
        ],
      },
    ],
    theorySections: [
      {
        title: "Applied vocal science & career awareness",
        items: [
          "Voice care for professionals",
          "Pre-performance preparation",
          "Studio vs live vocal adaptation basics",
        ],
      },
      {
        title: "Artistic development",
        items: [
          "Building a sustainable singing career",
          "Balancing interpretation and originality",
        ],
      },
    ],
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Professional Authority Level)",
    objective:
      "To certify complete technical freedom, stylistic authority, artistic individuality and leadership-level competence in Indian contemporary and film vocal performance.",
    assessmentFocus: [
      "Artistic authority",
      "Technical mastery",
      "Emotional depth",
      "Professional readiness",
      "Leadership and originality",
    ],
    examFormat: {
      title: "Level 6 — Mastery Certification",
      totalMarks: 100,
      practicalMarks: 80,
      theoryMarks: 20,
      components: [
        { criteria: "Practical — Mastery of Technique", marks: 30 },
        { criteria: "Practical — Artistic Identity & Originality", marks: 20 },
        { criteria: "Practical — Performance Leadership", marks: 15 },
        { criteria: "Practical — Endurance (40–45 min)", marks: 10 },
        { criteria: "Practical — Professional Presentation", marks: 5 },
        { criteria: "Theory / Viva / Research", marks: 20 },
      ],
      notes: "Successful candidates are awarded 'Global Certified Contemporary Indian Vocal Artist – Mastery Level'. This certification qualifies the holder to perform professionally, mentor intermediate students, and represent the certification body in performances and workshops.",
      passCriteria: "Minimum 60% + mandatory recital completion",
    },
    practicalSections: [
      {
        title: "Concert-level performance",
        items: [
          "40–45 minute continuous performance set with diverse Indian contemporary repertoire",
          "Demonstration of distinct artistic identity and originality",
        ],
      },
      {
        title: "Advanced ornamentation & stage leadership",
        items: [
          "Controlled runs, slides and expressive film-style gamaks",
          "Professional stage command and advanced microphone technique",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced vocal science & artistic development",
        items: [
          "Long-term voice sustainability",
          "Interpretive originality and artistic risk-taking",
        ],
      },
      {
        title: "Professional practice & mentorship",
        items: [
          "Repertoire planning, audition and recording awareness",
          "Introductory pedagogy and coaching contemporary vocal technique",
        ],
      },
    ],
  },
};
